import { Directive, Input, OnChanges } from '@angular/core';

@Directive({
  selector: '[appListSelection]',
  exportAs: 'appListSelection',
  standalone: false,
})
export class ListSelectionDirective<T extends object> implements OnChanges {
  @Input() items: T[] = [];
  @Input() selectionKey = 'id';

  private readonly selectedIds = new Set<string>();

  ngOnChanges(): void {
    const availableIds = new Set(this.items.map((item) => this.itemId(item)));
    for (const id of this.selectedIds) {
      if (!availableIds.has(id)) {
        this.selectedIds.delete(id);
      }
    }
  }

  get selectedItems(): T[] {
    return this.items.filter((item) => this.selectedIds.has(this.itemId(item)));
  }

  get singleSelection(): T | null {
    return this.selectedItems.length === 1 ? this.selectedItems[0] : null;
  }

  get selectionCount(): number {
    return this.selectedIds.size;
  }

  get hasSelection(): boolean {
    return this.selectionCount > 0;
  }

  get hasSingleSelection(): boolean {
    return this.selectionCount === 1;
  }

  get allSelected(): boolean {
    return this.items.length > 0 && this.items.every((item) => this.selectedIds.has(this.itemId(item)));
  }

  isSelected(record: T): boolean {
    return this.selectedIds.has(this.itemId(record));
  }

  toggle(record: T, selected: boolean): void {
    if (selected) {
      this.selectedIds.add(this.itemId(record));
      return;
    }
    this.selectedIds.delete(this.itemId(record));
  }

  toggleAll(selected: boolean): void {
    if (!selected) {
      this.clear();
      return;
    }
    for (const item of this.items) {
      this.selectedIds.add(this.itemId(item));
    }
  }

  clear(): void {
    this.selectedIds.clear();
  }

  private itemId(item: T): string {
    return `${(item as Record<string, unknown>)[this.selectionKey] ?? ''}`;
  }
}
